import React, { useMemo } from 'react'

import ProductIcon from '@mui/icons-material/Collections'
import { MenuItemLink, MenuProps, useTranslate } from 'react-admin'

import { Divider, Group, Indicator, Text } from '@mantine/core'
import type { PermissionApiDTO } from '../codegen/generated/Api.schemas'
import { useGetApiYoutubeQuotasLatest } from '../codegen/generated/youtube-quotas'
import { useUserStore } from '../stores/useUserStore'
import { MenuHelper } from './../helpers/Menu.helper'
import { SubMenu } from './SubMenu'

export const Menu = ({ dense = false }: MenuProps): JSX.Element => {
  const translate = useTranslate()
  const userStore = useUserStore()

  const youtubeQuotasQuery = useGetApiYoutubeQuotasLatest({
    query: { enabled: userStore.isAuthorised('youtubeQuotas.read') },
  })

  const quotasIndicator = useMemo(() => {
    const hasWarning = youtubeQuotasQuery.data?.body.some(
      q => q.usagePercent > 75 && q.usagePercent <= 95,
    )
    const hasError = youtubeQuotasQuery.data?.body.some(q => q.usagePercent > 95)

    if (hasWarning) {
      return { color: 'orange' }
    }

    if (hasError) {
      return { color: 'red' }
    }

    return
  }, [youtubeQuotasQuery.data?.body])

  return (
    <div>
      {userStore.isAuthorisedOneOf([
        'youtubeChannels.read',
        'beneficiaries.read',
        'products.read',
        'artworks.read',
      ]) && (
        <SubMenu name={translate('_generics.catalog')} icon={<ProductIcon />} dense={dense}>
          {userStore.isAuthorised('youtubeChannels.read') && (
            <MenuItemLink
              to="/youtubeChannels"
              primaryText={translate('pages.youtubeChannels._title')}
              leftIcon={<MenuHelper.icons.youtubeChannels />}
              dense={dense}
            />
          )}
          {userStore.isAuthorised('beneficiaries.read') && (
            <MenuItemLink
              to="/beneficiaries"
              primaryText={translate('pages.beneficiaries._title')}
              leftIcon={<MenuHelper.icons.beneficiaries />}
              dense={dense}
            />
          )}
          {userStore.isAuthorised('products.read') && (
            <MenuItemLink
              to="/products"
              primaryText={translate('pages.products._title')}
              leftIcon={<MenuHelper.icons.products />}
              dense={dense}
            />
          )}
          {userStore.isAuthorised('compilations.read') && (
            <MenuItemLink
              to="/compilations"
              primaryText={translate('pages.compilations._title')}
              leftIcon={<MenuHelper.icons.compilations />}
              dense={dense}
            />
          )}
          {userStore.isAuthorised('artworks.read') && (
            <MenuItemLink
              to="/artworks"
              primaryText={translate('pages.artworks._title')}
              leftIcon={<MenuHelper.icons.artworks />}
              dense={dense}
            />
          )}
        </SubMenu>
      )}
      {userStore.isAuthorised('imports.edit') && (
        <MenuItemLink
          to="/youtubeRawImports"
          primaryText={translate('pages.youtubeRawImports._title')}
          leftIcon={<MenuHelper.icons.youtubeRawImports />}
          dense={dense}
          disabled={!userStore.isAuthorised('imports.edit')}
        />
      )}
      {userStore.isAuthorised('statements.read') && (
        <MenuItemLink
          to="/statements"
          primaryText={translate('pages.statements._title')}
          leftIcon={<MenuHelper.icons.statements />}
          dense={dense}
          disabled={!userStore.isAuthorised('statements.read')}
        />
      )}
      {userStore.isAuthorised('dataviz.read') && (
        <MenuItemLink
          to="/analytics"
          primaryText={translate('pages.analytics._title')}
          leftIcon={<MenuHelper.icons.analytics />}
          dense={dense}
          disabled={!userStore.isAuthorised('dataviz.read')}
        />
      )}

      <Divider my="sm" />

      {userStore.isAuthorisedOneOf([
        'auto_monetization.read',
        'auto_translation.read',
        'auto_translation.edit',
      ]) && (
        <MenuItemLink
          to="/automated-treatments"
          primaryText={translate('pages.automatedTreatments._title')}
          leftIcon={<MenuHelper.icons.automatedTreatments />}
          dense={dense}
        />
      )}
      {userStore.isAuthorised('scam_export.read') && (
        <MenuItemLink
          to="/exports"
          primaryText={translate('pages.exports._title')}
          leftIcon={<MenuHelper.icons.exports />}
          dense={dense}
          disabled={!userStore.isAuthorised('scam_export.read')}
        />
      )}

      {userStore.isAuthorisedOneOf(['video_promotion.read', 'video_promotion.edit']) && (
        <MenuItemLink
          to="/videosPromotions"
          primaryText={translate('pages.videoPromotion._title')}
          leftIcon={<MenuHelper.icons.videoPromotion />}
          dense={dense}
          disabled={!userStore.isAuthorised('video_promotion.read')}
        />
      )}
      {userStore.isAuthorised('file_copy.read' as PermissionApiDTO) && (
        <MenuItemLink
          to="/file-copy"
          primaryText={translate('pages.fileCopy._title')}
          leftIcon={<MenuHelper.icons.fileCopy />}
          dense={dense}
          disabled={!userStore.isAuthorised('file_copy.edit' as PermissionApiDTO)}
        />
      )}
      {userStore.isAuthorised('youtubeQuotas.read') && (
        <MenuItemLink
          to="/quotas"
          primaryText={
            <Group justify="center">
              <Text c={quotasIndicator?.color}>{translate('pages.quotas._title')}</Text>
              {quotasIndicator && <Indicator size={10} color={quotasIndicator.color} processing />}
            </Group>
          }
          leftIcon={
            quotasIndicator ? <MenuHelper.icons.quotasError /> : <MenuHelper.icons.quotas />
          }
          color="red"
          dense={dense}
        />
      )}
      {userStore.isAuthorisedOneOf([
        'roles.read',
        'roles.edit',
        'users.edit',
        'user_roles.read',
        'user_rights.edit',
      ]) && <Divider my="sm" />}

      {userStore.isAuthorisedOneOf([
        'roles.read',
        'roles.edit',
        'users.edit',
        'user_roles.read',
        'user_rights.edit',
      ]) && (
        <MenuItemLink
          to="/users-roles"
          primaryText={translate('pages.users._menuTitle')}
          leftIcon={<MenuHelper.icons.users />}
          dense={dense}
        />
      )}
    </div>
  )
}
